import React from 'react'
import { styled, Typography } from '@mui/material'
import Card from '@zuto/atoms/src/Mui/Card/Card'
import withFade from '../../../HOC/withFade'
import { ApplicationFormAnswers, LenderDetails } from '../../../ApplicationForm/ApplicationFormContext'
import {
  Startline,
  Marsh,
  Zopa,
  AutoLend,
  Moneybarn,
  Moneyway,
  Oodle,
  CloseBrothersLogo,
  TandemLogo,
  CarmoolaLogo
} from '@zuto/icons'
import { sanitizeLenderName } from '../../../../utils/utils'

interface PrefillIntroductionProps {
  prefillData: ApplicationFormAnswers,
  isReapply?: boolean,
  isReviewPageV3PreApproval?: boolean,
  lenderDetails?: LenderDetails,
  isRequiredDetails?: boolean
}

const IntroTextWrapper = styled('div')({
  marginTop: '10px',
  'p:first-of-type': {
    margin: 0
  },
  '& .lender-logo-wrapper': {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 10
  }
})

export const LENDER_ICONS: Record<string, JSX.Element> = {
  startline: <Startline aria-label="Startline Logo" />,
  marsh: <Marsh aria-label="Marsh Logo" />,
  zopa: <Zopa aria-label="Zopa Logo" />,
  autolend: <AutoLend aria-label="Autolend Logo" />,
  moneybarn: <Moneybarn aria-label="Moneybarn Logo" />,
  moneyway: <Moneyway aria-label="Moneyway Logo" />,
  oodle: <Oodle aria-label="Oodle Logo" />,
  tandem: <TandemLogo aria-label="Tandem Logo" />,
  close: <CloseBrothersLogo aria-label="Close Logo" />,
  carmoola: <CarmoolaLogo aria-label="Carmoola Logo" />
}

const titleText = (name: string | undefined, isReapply: boolean) => isReapply ?
  <>Hey {name}, <br />Welcome back to Zuto</> :
  <>Hey {name}, <br />Welcome to Zuto</>

const summaryText = (isReapply: boolean, isReviewPageV3PreApproval: boolean = false, lenderDetails?: LenderDetails, isRequiredDetails?: boolean) => {
  const capitalizedLenderName = lenderDetails?.lenderName && sanitizeLenderName(lenderDetails?.lenderName)
  const lenderNameLowerCase = lenderDetails?.lenderName?.toLowerCase()
  const checkDetailsText = 'Check your details are correct and submit your application to continue through to your account!'

  if (isRequiredDetails) return (<>We&apos;ve got your details - we just need to<br />check a couple more things, then you can<br />submit your application and continue<br />through to Zuto.</>)

  if (isReviewPageV3PreApproval && lenderDetails) {
    if (lenderNameLowerCase === 'zuto') {
      return (
        <>
          Great news, you&apos;re pre-approved with a lender at Zuto. Check your details are correct and submit your application to continue.
        </>
      )
    }
    return (
      <>
        Check your details are correct and submit your application to continue through to your account.
      </>
    )
  }

  if (isReapply) {
    return (
      <>
        These are the details from your last car finance quote, does anything need updating?
        If yes, tap &apos;Edit details&apos; to make changes or continue and get a new quote.
      </>
    )
  }

  if (lenderNameLowerCase === 'zuto') {
    return (
      <>
        You&apos;re one step away from choosing your dream vehicle! {checkDetailsText}
      </>
    )
  }

  return (
    <>
      Thanks for selecting {capitalizedLenderName} powered by Zuto. {checkDetailsText}
    </>
  )
}

const bulletPoints = (isReapply: boolean) => {
  return isReapply ?
    <>
      Decision in 60 seconds <br />
      £0 deposit options
    </> :
    <>
      See if you&apos;ll be accepted <br />
      No impact on your credit score
    </>
}


const renderLenderLogo = (lenderDetails: LenderDetails) => {
  const lenderName = lenderDetails?.lenderName?.toLowerCase() as keyof typeof LENDER_ICONS
  return lenderName && LENDER_ICONS[lenderName] ? LENDER_ICONS[lenderName] : null
}

const PrefillIntroductionV3 = ({
  prefillData,
  isReapply = false,
  isReviewPageV3PreApproval = false,
  lenderDetails,
  isRequiredDetails = false
}: PrefillIntroductionProps): JSX.Element => {
  const isPreapprovedLenderNotZuto = isReviewPageV3PreApproval && lenderDetails && lenderDetails?.lenderName?.toLowerCase() !== 'zuto'

  return (
    <>
      <Typography variant="h2">{titleText(prefillData.personalDetails?.firstName, isReapply)}</Typography>
      <IntroTextWrapper>
        <Card styleOverrides={{ marginBottom: isReviewPageV3PreApproval ? 6 : 0, padding: '20px' }}>
          {isPreapprovedLenderNotZuto && (
            <div className="lender-logo-wrapper">
              {renderLenderLogo(lenderDetails)}
            </div>
          )}
          {isPreapprovedLenderNotZuto && <>
            <Typography variant="rowHeading" align='center'>Congratulations, you&apos;ve been</Typography>
            <Typography variant="rowHeading" align='center' mb="10px">pre-approved for car finance!</Typography>
          </>}
          <Typography variant="body1" align={isPreapprovedLenderNotZuto ? 'center' : 'left'}>
            {summaryText(isReapply, isReviewPageV3PreApproval, lenderDetails, isRequiredDetails)}
          </Typography>
        </Card>
      </IntroTextWrapper>
      {!isReviewPageV3PreApproval &&
        <Typography variant="body2" sx={{ textAlign: 'center', margin: '10px 0' }}>
          {bulletPoints(isReapply)}
        </Typography>
      }
    </>
  )
}

export default withFade(PrefillIntroductionV3)
